/**
 * SOLID Review
 * S — Strong. One job: reload the page, guarded against reload loops via a sessionStorage timestamp. The
 *     extensive header comment documents exactly why it exists (last-resort recovery after
 *     §13.30/§13.33/§13.35 can't fix a stuck mobile subsystem in place) and does nothing beyond that.
 * O — Strong. RELOAD_GUARD_MS is the one tunable; no other extension axis exists for "reload the page
 *     safely." N/A /
 * L — N/A — no inheritance hierarchy, a single exported function.
 * I — Strong. Single function, single call shape — nothing to split.
 * D — Strong. No imports at all; talks directly to sessionStorage and window.location.
 *
 *     RELOCATED infrastructure/recovery/ -> shared/ on 2026-08-17. The verdict was "Acceptable"
 *     while this sat in infrastructure/, because a framework component importing it read as a
 *     layering violation and two such edges were baselined. Nothing about the file needed to
 *     change: a guarded window.location.reload() is shared plumbing, not a providing adapter, and
 *     shared/ is where it belongs. Same reasoning as tabPersistentStorage's move in the same pass.
 */
// Full-page reload as the reliable last-resort recovery.
//
// On mobile, returning to a tab the OS has heavily frozen/GPU-starved can leave a
// subsystem permanently stuck (a re-lost WebGL context, a socket that won't
// revive) that the incremental recoveries (§13.30 reconnect, §13.33 canvas
// remount, §13.35 re-sync) can't fix in place — but a manual refresh always does.
// Now that identity is durable (§13.34/§13.36) and §13.33 auto-resumes into the
// live game, a reload is safe: the player lands straight back where they were.
//
// Guarded against reload loops: a reload is suppressed if one happened within the
// last RELOAD_GUARD_MS. The timestamp lives in sessionStorage, which survives a
// programmatic reload (same tab session) but resets on a tab discard.

const RELOAD_GUARD_KEY = 'ml3d_last_reload_at';
const RELOAD_GUARD_MS = 20_000;

export function safeReload(): void {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || '0');
    if (Date.now() - last < RELOAD_GUARD_MS) return; // already reloaded very recently — don't loop
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    /* sessionStorage unavailable — proceed without the guard */
  }
  try {
    window.location.reload();
  } catch {
    /* not reloadable (e.g. test env) */
  }
}
